import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * NewDocsBanner (fs-7 08.2 + 5.1) — a sticky "you have new documents" banner for
 * the client portal, driven by REAL unseen-document state from
 * GET /api/client-portal/banner ({ unviewed_count, documents }). A document is
 * "unseen" when the firm shared it (notify_client=1) and this portal user has no
 * document_views row for it yet; opening/downloading it (POST .../viewed) writes
 * that row, which drops the count on the next fetch.
 *
 * Dismissal (5.1): the banner carries a top-right X. Both the X and the
 * "Review documents" button ACKNOWLEDGE the currently-unviewed doc ids — the ids
 * are stored in a session-scoped set (sessionStorage `cp:newdocs:ack`) and the
 * banner only surfaces docs whose id is NOT acknowledged. Because acknowledgement
 * is keyed to doc ids, a genuinely-new share (a new id) re-surfaces the banner
 * automatically, while a stale acknowledged id can never permanently suppress a
 * real new document. Acknowledgement is UI-only — it never writes document_views,
 * so it never fires a false "client viewed" event to the firm.
 *
 * Hidden while the Documents tab is active (the user is already looking at them)
 * and when nothing unacknowledged is unseen. "Review documents" jumps to the
 * Documents tab AND acknowledges, so it clears everywhere for the session.
 */

interface BannerDoc { id: string; name: string; document_type: string | null }
interface BannerState { unviewed_count: number; documents: BannerDoc[] }

const ACK_KEY = 'cp:newdocs:ack';

function loadAck(): Set<string> {
  try {
    const raw = sessionStorage.getItem(ACK_KEY);
    const arr = raw ? JSON.parse(raw) : null;
    return Array.isArray(arr) ? new Set(arr.filter((x): x is string => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function saveAck(set: Set<string>): void {
  try { sessionStorage.setItem(ACK_KEY, JSON.stringify([...set])); } catch { /* sessionStorage unavailable — in-memory set still works */ }
}

export function NewDocsBanner({ onOpenDocuments, isActive }: { onOpenDocuments: () => void; isActive: boolean }) {
  const q = useQuery({
    queryKey: ['cp', 'banner'],
    queryFn: ({ signal }) => api.get<BannerState>('/api/client-portal/banner', signal),
    // Cheap poll so a doc shared while the client is on another tab surfaces
    // without a manual reload; refetch on focus for the returning-tab case.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const [ack, setAck] = useState<Set<string>>(loadAck);

  const docs = q.data?.documents ?? [];
  // What the banner actually surfaces: unviewed docs not yet acknowledged this
  // session. A new share (new id) is never in `ack`, so it re-surfaces here.
  const pending = docs.filter((d) => !ack.has(d.id));

  // Acknowledge every currently-unviewed doc (superset of `pending`) so the banner
  // clears on EVERY tab for the session. Persisted so an accidental reload keeps it
  // dismissed. Never writes document_views — this is a UI acknowledgement only.
  const acknowledgeAll = useCallback(() => {
    setAck((prev) => {
      const next = new Set(prev);
      for (const d of docs) next.add(d.id);
      saveAck(next);
      return next;
    });
  }, [docs]);

  if (isActive || pending.length === 0) return null;

  const count = pending.length;
  const first = pending[0]?.name;
  const label = count === 1
    ? `You have a new document${first ? `: ${first}` : ''}`
    : `You have ${count} new documents`;

  return (
    <div
      role="status"
      style={{
        position: 'sticky', top: 0, zIndex: 20,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        margin: '0 0 20px', padding: '12px 16px', borderRadius: 10,
        background: 'var(--color-status-green-bg, #e8f5ee)',
        border: '1px solid var(--color-status-green, #14532d)',
        color: 'var(--color-status-green, #14532d)',
      }}
    >
      <svg aria-hidden width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{label}</span>
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          onClick={() => { acknowledgeAll(); onOpenDocuments(); }}
          style={{
            appearance: 'none', cursor: 'pointer',
            padding: '6px 14px', fontSize: 13, fontWeight: 600, borderRadius: 999,
            border: '1px solid var(--color-status-green, #14532d)',
            background: 'var(--color-status-green, #14532d)',
            color: 'var(--brand-on-primary, #fff)',
          }}
        >
          Review documents
        </button>
        <button
          type="button"
          aria-label="Dismiss"
          title="Dismiss"
          onClick={acknowledgeAll}
          style={{
            appearance: 'none', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: 999, fontSize: 18, lineHeight: 1,
            border: '1px solid var(--color-status-green, #14532d)',
            background: 'transparent',
            color: 'var(--color-status-green, #14532d)',
          }}
        >
          <span aria-hidden>×</span>
        </button>
      </div>
    </div>
  );
}
